import { observable, action } from 'mobx'

import { StoreExt } from '@utils/reactExt'
import { schema_terms } from 'adv-lib/src/models/web/terms'
import {
  IJoinUsableAccount,
  IJoinUsableEmail,
  IJoinUsableInvitorNickName,
  IJoinUsableNickName,
  IJoinUsableRecommender,
  IJoinUser,
} from '@protocol/join'
import { ACCOUNT_TYPE } from '@interface/common'
import { message } from 'antd'
import { SocialLoginParams } from '@store/authStore'
import { authStore, globalStore } from '@store/index'
import { ISignin } from '@protocol/auth'
import { GAME_KIND } from '@v2/constants/common'
import { IDroppedUser } from '@protocol/user'
import { ISignupQuizSubmit } from '@protocol/signup-quiz'
import { Schema$SignupQuizItem, Schema$SignupQuizReward } from '@interface/signup-quiz'

// 가입 QUIZ 결과 상태 — JoinEnd 완료 화면 분기에 사용
export type SignupQuizResultState = 'correct' | 'wrong' | 'skip'

export class JoinStore extends StoreExt {

  @observable
  accountType: ACCOUNT_TYPE

  @observable
  termsAgreed: boolean

  @observable
  policyAgreed: boolean

  @observable
  snsAgreed: boolean

  @observable
  joinTerms: Partial<schema_terms>[]

  registeredUserAccounts: string[]

  registeredUserAccountType: ACCOUNT_TYPE

  @observable
  civalue: string

  socialLoginParams: ISignin.SocialLoginParams

  gameKind: GAME_KIND

  @observable
  isJoinUrl: boolean

  // ── 가입 QUIZ 이벤트 ──
  @observable
  signupQuizzes: Schema$SignupQuizItem[]

  @observable
  signupQuizReward: Schema$SignupQuizReward

  @observable
  signupQuizResultState: SignupQuizResultState

  constructor() {
    super()
    console.timeEnd('Mobx store initializing finished!!')
  }

  @action
  cleanup = (isAll = false) => {
    this.termsAgreed = false
    this.policyAgreed = false
    this.snsAgreed = false
    this.registeredUserAccounts = undefined
    this.registeredUserAccountType = undefined
    this.civalue = undefined
    this.joinTerms = undefined
    this.gameKind = GAME_KIND.BETTING
    this.signupQuizzes = undefined
    this.signupQuizReward = undefined
    this.signupQuizResultState = undefined

    // 약관동의 페이지 진입시 유지되어야 할 값도 모두 초기화 한다. 회원 가입이 완료 되었을 경우 사용
    if (isAll) {
      this.socialLoginParams = undefined
      this.accountType = ACCOUNT_TYPE.LOCAL
      this.isJoinUrl = false
    }
  }

  @action
  toggleTermsAgreed = () => {
    this.termsAgreed = !this.termsAgreed
  }

  @action
  togglePolicyAgreed = () => {
    this.policyAgreed = !this.policyAgreed
  }

  @action
  toggleSnsAgreed = () => {
    this.snsAgreed = !this.snsAgreed
  }

  @action
  setSnsAgreed = (agreed: boolean) => {
    this.snsAgreed = agreed
  }

  @action
  setTermsAgreed = (agreed: boolean) => {
    this.termsAgreed = agreed
  }

  @action
  setPolicyAgreed = (agreed: boolean) => {
    this.policyAgreed = agreed
  }

  @action
  setJoinTerms = joinTerms => {
    this.joinTerms = joinTerms
  }

  @action
  getJoinTerms = async () => {
    try {
      const { joinTerms } = await this.api.join.getJoinTerms({})
      this.setJoinTerms(joinTerms)
    } catch (err) {
      message.error(err.message)
    }
  }

  @action
  setAccountType = (type: ACCOUNT_TYPE) => {
    this.accountType = type
  }

  isRegisteredUser = async (civalue: string, type: ACCOUNT_TYPE): Promise<boolean> => {
    let isRegistered = false

    try {
      const result = await this.api.user.getUserAccounts({ civalue, type })
      const { userAccounts } = result
      if (userAccounts.length > 0) {
        isRegistered = true
        this.registeredUserAccounts = userAccounts
        this.registeredUserAccountType = type
      }
    } catch (err) {
      message.error(err.message)
    }

    return isRegistered
  }

  getNotReJoinableDroppedUser = async (civalue: string, type: ACCOUNT_TYPE): Promise<IDroppedUser.Schema> => {
    let result
    try {
      result = await this.api.user.getNotReJoinableDroppedUser({ civalue, type })
    } catch (err) {
      message.error(err.message)
    }
    return result
  }

  checkUsableAccount = async (account: string): Promise<IJoinUsableAccount.Schema> => {
    let result = {
      isUnregisteredAccount: true,
      isDuplicatedAccount: true,
    }

    try {
      result = await this.api.join.checkUsableAccount({ account })
    } catch (err) {
      message.error(err.message)
    }

    return result
  }

  checkUsableNickName = async (nickName: string): Promise<IJoinUsableNickName.Schema> => {
    let result = {
      isDuplicated: true,
      isForbidWord: true,
    }

    try {
      result = await this.api.join.checkUsableNickName({ nickName })
    } catch (err) {
      message.error(err.message)
    }

    return result
  }

  checkUsableEmail = async (email: string): Promise<IJoinUsableEmail.Schema> => {
    let result = {
      isDuplicated: true,
    }

    try {
      result = await this.api.join.checkUsableEmail({ email })
    } catch (err) {
      message.error(err.message)
    }

    return result
  }

  checkUsableRecommender = async (recommender: string): Promise<IJoinUsableRecommender.Schema> => {
    let result = {
      isUsable: true,
    }

    try {
      result = await this.api.join.checkUsableRecommender({ recommender })
    } catch (err) {
      message.error(err.message)
    }

    return result
  }

  checkUsableInvitorNickName = async (nickName: string): Promise<IJoinUsableInvitorNickName.Schema> => {
    let result = {
      isUsable: false,
    }

    try {
      result = await this.api.join.checkUsableInvitorNickName({ nickName, civalue: this.civalue })
    } catch (err) {
      message.error(err.message)
    }

    return result
  }

  @action
  setCivalue = (civalue: string) => {
    this.civalue = civalue
  }

  joinUser = async (data: IJoinUser.Params) => {
    try {
      const result = await this.api.join.joinUser(data)
      return result
    } catch (err) {
      message.error(err.message)
    }
    return null
  }

  reAuthentication = async (civalue: string) => {
    try {
      const userInfo = authStore.userInfo
      const result = await this.api.join.reAuthentication({ civalue, userPromiseId: userInfo?.id })
      return result
    } catch (err) {
      message.error(err.message)
    }
    return null
  }

  checkUserCivalue = async (civalue: string) => {
    try {
      const userInfo = authStore.userInfo
      const result = await this.api.join.checkUserCivalue({ civalue, userPromiseId: userInfo?.id })
      return result
    } catch (err) {
      message.error(err.message)
    }
    return null
  }

  // 가입 QUIZ 노출 여부 + 문항/보상 조회 (노출 시 store에 적재)
  fetchSignupQuizInfo = async (civalue: string): Promise<boolean> => {
    try {
      const result = await this.api.signupQuiz.info({ civalue })
      if (!result || !result.exposed) {
        return false
      }
      this.setSignupQuizInfo(result.quizzes, result.reward)
      return true
    } catch (err) {
      // 노출 조회 실패 시 QUIZ를 건너뛰고 기존 완료 플로우로 진행
      return false
    }
  }

  @action
  setSignupQuizInfo = (quizzes: Schema$SignupQuizItem[], reward: Schema$SignupQuizReward) => {
    this.signupQuizzes = quizzes
    this.signupQuizReward = reward
  }

  @action
  setSignupQuizResultState = (state: SignupQuizResultState) => {
    this.signupQuizResultState = state
  }

  // QUIZ 답 제출 → 결과 상태 적재 (정답/오답)
  submitSignupQuiz = async (
    userPromiseId: string,
    answers: ISignupQuizSubmit.Params['answers'],
  ): Promise<boolean> => {
    try {
      const result = await this.api.signupQuiz.submit({ civalue: this.civalue, userPromiseId, answers })
      if (!result || (result as any).error) {
        message.error('제출에 실패했습니다. 잠시 후 다시 시도해주세요.')
        return false
      }
      this.setSignupQuizResultState(result.isCorrect ? 'correct' : 'wrong')
      return true
    } catch (err) {
      message.error(err.message)
      return false
    }
  }

  // QUIZ 건너뛰기 → 결과 상태(skip) 적재
  skipSignupQuiz = async (userPromiseId: string): Promise<boolean> => {
    try {
      await this.api.signupQuiz.skip({ civalue: this.civalue, userPromiseId })
      this.setSignupQuizResultState('skip')
      return true
    } catch (err) {
      // 건너뛰기 기록 실패해도 완료 플로우는 진행 (skip 상태로 처리)
      this.setSignupQuizResultState('skip')
      return true
    }
  }

  setSocialLoginParams = (params: SocialLoginParams) => {
    this.socialLoginParams = params
  }

  @action
  setIsJoinUrl = (isJoinUrl: boolean) => {
    this.isJoinUrl = isJoinUrl
  }

  showNotSupportedBrowserDialog = () => {
    globalStore.pushDialogOk({
      title: '알림',
      text: ['회원가입을 지원하지 않는 브라우저입니다.', '다른 브라우저를 이용해 주십시오.'],
      className: 'small-size',
      callbackCloseDialog: () => {
        globalStore.closeDialog()
      },
    })
  }

  setGameKind = (gameKind: GAME_KIND) => {
    this.gameKind = gameKind
  }
}

export default new JoinStore()
